/**
 * Chart.js, fetched on demand with only the parts the shared charts draw registered — the
 * way into the library, as `loadCharts` is for amCharts.
 *
 * A chart type that is not listed below throws `"x" is not a registered controller` — add
 * it to the destructure **and** the `register` call. Never hand the module namespace on
 * (`import('chart.js').then((lib) => lib)`), nor `import 'chart.js/auto'`: either keeps
 * every controller and scale in the chunk, which is the whole of Chart.js.
 */

import type { Chart } from 'chart.js'

import { showGlobalError } from '../globalErrorDisplay'

export interface ChartJs {
  Chart: typeof Chart
}

let pending: Promise<ChartJs> | null = null

/** As `loadCharts`: fetched once, a failed fetch is not remembered, and it re-throws. */
export function loadChartJs(): Promise<ChartJs> {
  pending ??= import('chart.js')
    .then(
      ({
        Chart,
        BarController,
        BarElement,
        CategoryScale,
        LinearScale,
        PieController,
        ArcElement,
        LineController,
        LineElement,
        PointElement,
        Filler,
        Tooltip,
        Legend
      }) => {
        Chart.register(
          BarController,
          BarElement,
          CategoryScale,
          LinearScale,
          PieController,
          ArcElement,
          LineController,
          LineElement,
          PointElement,
          Filler,
          Tooltip,
          Legend
        )

        return { Chart }
      }
    )
    .catch((error) => {
      pending = null
      showGlobalError(error, 'Could not load the charts library.')

      throw error
    })

  return pending
}
